import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck, ShieldAlert, ShieldX, Plus, Trash2,
  Building2, Calendar, User, AlertTriangle
} from "lucide-react";
import { cn } from "@/lib/utils";

const CERT_TYPES = [
  "CP12 (LGSR)",
  "Gas Safety Certificate",
  "Boiler Service Certificate",
  "Gas Tightness Test",
  "Commissioning Certificate",
  "Other",
];

const statusConfig = {
  valid: { label: "Valid", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", Icon: ShieldCheck },
  expiring_soon: { label: "Expiring Soon", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", Icon: ShieldAlert },
  expired: { label: "Expired", cls: "bg-red-500/10 text-red-400 border-red-500/20", Icon: ShieldX },
  unknown: { label: "Unknown", cls: "bg-white/5 text-muted-foreground border-white/10", Icon: ShieldCheck },
};

function daysUntilExpiry(expiry_date) {
  return Math.ceil((new Date(expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
}

const EMPTY_FORM = {
  lead_id: "", cert_type: "CP12 (LGSR)", cert_number: "",
  issue_date: "", expiry_date: "", engineer_name: "", notes: ""
};

// ── Skeleton table row ──
function CertSkeleton() {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 md:p-4 rounded-sm border border-white/5 bg-card">
      <Skeleton className="w-9 h-9 rounded-sm flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3.5 w-40" />
        <div className="flex gap-3">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <Skeleton className="h-4 w-20 rounded-sm" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  );
}

export default function CertificatesPage() {
  const [certificates, setCertificates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [renewingSoonFilter, setRenewingSoonFilter] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchCertificates = useCallback(async () => {
    try {
      const res = await api.get("/certificates");
      setCertificates(res.data);
    } catch {
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchCertificates();
    fetchLeads();
  }, [fetchCertificates, fetchLeads]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/certificates/${id}`);
      toast.success("Certificate deleted");
      fetchCertificates();
    } catch {
      toast.error("Failed to delete certificate");
    }
  };

  const handleSubmit = async () => {
    if (!form.lead_id || !form.issue_date || !form.expiry_date) {
      toast.error("Lead, issue date and expiry date are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/certificates", form);
      toast.success("Certificate added");
      setShowForm(false);
      setForm(EMPTY_FORM);
      fetchCertificates();
    } catch {
      toast.error("Failed to add certificate");
    } finally {
      setSaving(false);
    }
  };

  // Certs expiring within 30 days (not yet expired)
  const renewingSoonCount = useMemo(() => {
    return certificates.filter(c => {
      if (!c.expiry_date) return false;
      const days = daysUntilExpiry(c.expiry_date);
      return days > 0 && days <= 30;
    }).length;
  }, [certificates]);

  const filtered = useMemo(() => {
    let result = statusFilter === "all" ? certificates : certificates.filter(c => c.status === statusFilter);
    if (renewingSoonFilter) {
      result = result.filter(c => {
        if (!c.expiry_date) return false;
        const days = daysUntilExpiry(c.expiry_date);
        return days > 0 && days <= 30;
      });
    }
    return result;
  }, [certificates, statusFilter, renewingSoonFilter]);

  const counts = {
    all: certificates.length,
    valid: certificates.filter(c => c.status === "valid").length,
    expiring_soon: certificates.filter(c => c.status === "expiring_soon").length,
    expired: certificates.filter(c => c.status === "expired").length,
  };

  return (
    <div data-testid="certificates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
        <div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Gas Safety Certs
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">Track CP12s and safety certificate expiry dates</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-5"
          data-testid="add-cert-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Certificate
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { key: "all", label: "Total Certs", color: "text-foreground" },
          { key: "valid", label: "Valid", color: "text-emerald-400" },
          { key: "expiring_soon", label: "Expiring Soon", color: "text-amber-400" },
          { key: "expired", label: "Expired", color: "text-red-400" },
        ].map(s => (
          <Card key={s.key} className="border-white/5 bg-card rounded-sm">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</p>
              <p className={cn("text-2xl font-bold mt-1", s.color)} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {loading ? <Skeleton className="h-7 w-8 inline-block" /> : counts[s.key]}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Renewing Soon alert banner */}
      {!loading && renewingSoonCount > 0 && (
        <button
          onClick={() => setRenewingSoonFilter(f => !f)}
          data-testid="renewing-soon-filter"
          className={cn(
            "w-full flex items-center gap-3 px-4 py-2.5 rounded-sm border mb-4 text-left transition-colors",
            renewingSoonFilter
              ? "bg-red-500/15 border-red-500/30 text-red-400"
              : "bg-red-500/5 border-red-500/20 text-red-400 hover:bg-red-500/10"
          )}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-medium uppercase tracking-wider">
            {renewingSoonCount} {renewingSoonCount === 1 ? "cert" : "certs"} expiring within 30 days
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-wider opacity-70">
            {renewingSoonFilter ? "Clear filter" : "Show only"}
          </span>
        </button>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-1.5 flex-wrap mb-4">
        {[
          { value: "all", label: "All" },
          { value: "valid", label: "Valid" },
          { value: "expiring_soon", label: "Expiring Soon" },
          { value: "expired", label: "Expired" },
        ].map(s => (
          <button
            key={s.value}
            onClick={() => { setStatusFilter(s.value); setRenewingSoonFilter(false); }}
            data-testid={`filter-cert-${s.value}`}
            className={cn(
              "px-3 py-1.5 text-xs rounded-sm border uppercase tracking-wider transition-colors",
              statusFilter === s.value && !renewingSoonFilter
                ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                : "text-muted-foreground border-white/5 hover:bg-white/5 hover:text-foreground"
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Loading skeletons ── */}
      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <CertSkeleton key={i} />
          ))}
        </div>
      )}

      {/* ── Certificate List ── */}
      {!loading && (
        <div className="space-y-2">
          {filtered.length === 0 && certificates.length === 0 ? (
            /* True empty state */
            <Card className="border-white/5 bg-card rounded-sm">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <ShieldCheck className="w-12 h-12 text-muted-foreground/20 mb-4" strokeWidth={1} />
                <p className="text-base font-semibold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  No certificates yet
                </p>
                <p className="text-sm text-muted-foreground mt-1 mb-5">
                  Track gas safety certificates and expiry dates
                </p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-9 px-5"
                  data-testid="empty-add-cert-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Certificate
                </Button>
              </CardContent>
            </Card>
          ) : filtered.length === 0 ? (
            /* Filtered empty */
            <Card className="border-white/5 bg-card rounded-sm">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <ShieldCheck className="w-10 h-10 text-muted-foreground/30 mb-3" strokeWidth={1} />
                <p className="text-sm text-muted-foreground">No certificates match the current filter</p>
              </CardContent>
            </Card>
          ) : (
            filtered.map(cert => {
              const sc = statusConfig[cert.status] || statusConfig.unknown;
              const StatusIcon = sc.Icon;
              const days = daysUntilExpiry(cert.expiry_date);
              return (
                <div
                  key={cert.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 md:p-4 rounded-sm border border-white/5 bg-card hover:bg-white/[0.02] transition-colors"
                  data-testid={`cert-row-${cert.id}`}
                >
                  <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0", sc.cls.split(' ')[0])}>
                    <StatusIcon className={cn("w-5 h-5", sc.cls.split(' ')[1])} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground">{cert.cert_type}</p>
                      {cert.cert_number && (
                        <span className="text-xs text-muted-foreground font-mono">#{cert.cert_number}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {cert.company_name}
                      </div>
                      {cert.engineer_name && (
                        <div className="flex items-center gap-1" data-testid={`cert-engineer-${cert.id}`}>
                          <User className="w-3 h-3" />
                          {cert.engineer_name}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Issued {new Date(cert.issue_date).toLocaleDateString("en-GB")}
                      </div>
                    </div>
                    {cert.notes && (
                      <p className="text-xs text-muted-foreground/60 mt-1 line-clamp-1">{cert.notes}</p>
                    )}
                  </div>
                  <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-1.5 flex-shrink-0">
                    <Badge variant="outline" className={cn("text-[10px] rounded-sm border", sc.cls)}>
                      {sc.label}
                    </Badge>
                    <div className="text-[10px] text-muted-foreground">
                      Expires {new Date(cert.expiry_date).toLocaleDateString("en-GB")}
                    </div>
                    {cert.status !== "expired" && days > 0 && (
                      <span className={cn("text-[10px]", days <= 30 ? "text-amber-400" : "text-muted-foreground/60")}>
                        {days}d remaining
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(cert.id)}
                    className="text-muted-foreground/30 hover:text-red-400 transition-colors self-start sm:self-center"
                    data-testid={`delete-cert-${cert.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Add Certificate Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-white/10 rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Add Certificate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Lead / Company *</Label>
              <Select value={form.lead_id} onValueChange={v => setForm(f => ({ ...f, lead_id: v }))}>
                <SelectTrigger className="rounded-sm border-white/10 bg-background text-sm h-9">
                  <SelectValue placeholder="Select a lead..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {leads.map(lead => (
                    <SelectItem key={lead.id} value={lead.id} className="text-sm">{lead.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Certificate Type</Label>
              <Select value={form.cert_type} onValueChange={v => setForm(f => ({ ...f, cert_type: v }))}>
                <SelectTrigger className="rounded-sm border-white/10 bg-background text-sm h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {CERT_TYPES.map(t => (
                    <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Certificate Number</Label>
              <Input
                value={form.cert_number}
                onChange={e => setForm(f => ({ ...f, cert_number: e.target.value }))}
                placeholder="e.g. GS-2024-001"
                className="rounded-sm border-white/10 bg-background text-sm h-9"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Issue Date *</Label>
                <Input
                  type="date"
                  value={form.issue_date}
                  onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))}
                  className="rounded-sm border-white/10 bg-background text-sm h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Expiry Date *</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                  className="rounded-sm border-white/10 bg-background text-sm h-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Engineer Name</Label>
              <Input
                value={form.engineer_name}
                onChange={e => setForm(f => ({ ...f, engineer_name: e.target.value }))}
                placeholder="e.g. John Smith"
                className="rounded-sm border-white/10 bg-background text-sm h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="rounded-sm border-white/10 bg-background text-sm resize-none"
                rows={2}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
                className="flex-1 rounded-sm border-white/10 text-xs uppercase tracking-wider"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 rounded-sm bg-blue-500 hover:bg-blue-600 text-white text-xs uppercase tracking-wider"
              >
                {saving ? "Saving..." : "Add Certificate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
